
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, DocumentTextIcon, CurrencyDollarIcon, UsersIcon, ChartBarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, ChartPieIcon, ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import { transformSalesData, calculateSalesStats, groupSalesByPeriod } from '../../utils/salesDataUtils';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const SalesReports = ({ onBack, documents, products, customers, suppliers }) => {
  const [selectedReport, setSelectedReport] = useState('sales-by-period');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [isGenerating, setIsGenerating] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [productSalesData, setProductSalesData] = useState([]);
  const [clientSalesData, setClientSalesData] = useState([]);
  // REMOVED: sellerPerformanceData - not available in real sales data
  const [salesTrends, setSalesTrends] = useState([]);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  // Use passed props instead of context calls for better performance
  // Fallback to context if props not provided (backwards compatibility)
  const { 
    products: contextProducts, 
    sales, 
    customers: contextCustomers, 
    purchases 
  } = useAppData();
  const { documents: contextDocuments } = useDocuments();
  const { companySettings } = useCompanySettings();
  
  // Use props if available, otherwise fallback to context
  const finalProducts = products || contextProducts;
  const finalCustomers = customers || contextCustomers;
  const finalDocuments = documents || contextDocuments;
  
  // Debugging: Log available data - ENHANCED DEBUGGING
  console.log('🔍 SalesReports Data:', {
    salesCount: sales?.length || 0,
    productsCount: finalProducts?.length || 0,
    customersCount: finalCustomers?.length || 0,
    documentsCount: finalDocuments?.length || 0,
    salesSample: sales?.[0],
    salesData: sales,
    productsSample: finalProducts?.[0],
    customersSample: finalCustomers?.[0],
    documentsSample: finalDocuments?.[0],
    salesItemsSample: sales?.[0]?.items
  });
  
  // Additional logging for debugging
  if (sales && sales.length > 0) {
    console.log('💰 Sales data found:', sales.map(s => ({
      id: s.id,
      date: s.date,
      total: s.total,
      items: s.items?.length || 0,
      customerId: s.customerId
    })));
  } else {
    console.log('⚠️ No sales data in AppDataContext');
  }
  
  if (finalDocuments && finalDocuments.length > 0) {
    console.log('📄 Documents data found:', finalDocuments.map(d => ({
      id: d.id,
      type: d.type,
      date: d.date,
      total: d.total
    })));
  } else {
    console.log('⚠️ No documents data in DocumentsContext');
  }
  
  // Debug customer data specifically
  if (finalCustomers && finalCustomers.length > 0) {
    console.log('👥 Available customers:', finalCustomers.map(c => ({
      id: c.id,
      name: `${c.firstName} ${c.lastName}`,
      company: c.company
    })));
  }
  
  // Types de rapports de ventes
  const reportTypes = [
    {
      id: 'sales-by-period',
      name: 'Ventes par période',
      description: 'Analyse des ventes par jour, semaine, mois ou année',
      icon: ChartBarIcon
    },
    {
      id: 'sales-by-product',
      name: 'Ventes par produit',
      description: 'Répartition des ventes par produit ou catégorie',
      icon: DocumentTextIcon
    },
    {
      id: 'sales-by-client',
      name: 'Ventes par client',
      description: 'Analyse des achats par client',
      icon: UsersIcon
    },
    {
      id: 'sales-comparison',
      name: 'Comparaisons et tendances',
      description: 'Analyse comparative des ventes et tendances',
      icon: ArrowTrendingUpIcon
    }
  ];
  
  // Couleurs pour les graphiques
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  // Memoize sales data using SHARED transformation logic - ensures consistency with SalesPage
  const memoizedSalesData = useMemo(() => {
    console.log('🔄 Building sales data using SHARED transformation logic');
    console.log('📊 Raw data sources:', {
      salesCount: sales?.length || 0,
      documentsCount: finalDocuments?.length || 0,
      customersCount: finalCustomers?.length || 0,
      salesSample: sales?.[0],
      documentsSample: finalDocuments?.[0]
    });

    // START WITH SALES FROM CONTEXT (MAIN SOURCE)
    let allSalesData = [];

    // Add sales from AppDataContext (main source)
    if (sales && sales.length > 0) {
      const salesFromContext = sales.map(sale => {
        console.log('🔍 Processing sale:', {
          saleId: sale.id,
          customerId: sale.customerId,
          customerName: sale.customerName,
          availableCustomers: finalCustomers?.map(c => ({ id: c.id, firstName: c.firstName, lastName: c.lastName, company: c.company, name: c.name }))
        });
        
        const customer = finalCustomers?.find(c => 
          c.id == sale.customerId || 
          c.id === sale.customerId || 
          c.id === String(sale.customerId)
        );
        
        console.log('👤 Found customer:', {
          customerId: sale.customerId,
          foundCustomer: customer,
          customerDetails: customer ? {
            id: customer.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            company: customer.company,
            name: customer.name
          } : null
        });
        
        // Enhanced customer name resolution with better fallbacks
        let customerName = 'Client inconnu';
        
        if (sale.customerName && sale.customerName.trim()) {
          customerName = sale.customerName.trim();
        } else if (customer) {
          // Try different combinations to get a proper name
          if (customer.company && customer.company.trim()) {
            customerName = customer.company.trim();
          } else if (customer.name && customer.name.trim()) {
            customerName = customer.name.trim();
          } else if (customer.firstName || customer.lastName) {
            const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
            if (fullName) {
              customerName = fullName;
            }
          } else if (customer.email) {
            customerName = customer.email.split('@')[0]; // Use email prefix as fallback
          }
        }
        
        // Calculate total from items if available
        const amount = sale.items && Array.isArray(sale.items) 
          ? sale.items.reduce((sum, item) => sum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
          : parseFloat(sale.total || 0);

        return {
          date: new Date(sale.date || sale.createdAt || Date.now()).toLocaleDateString('fr-FR'),
          reference: sale.invoiceNumber || sale.id,
          client: customerName,
          amount: `${amount.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`,
          status: sale.status === 'completed' ? 'Payée' : 'En attente',
          source: 'sales'
        };
      });
      allSalesData.push(...salesFromContext);
    }

    // Add invoices and quotes from documents
    const salesDocuments = finalDocuments?.filter(doc => 
      doc.type === 'invoice' || doc.type === 'quote'
    ) || [];

    if (salesDocuments.length > 0) {
      const documentsData = salesDocuments.map(doc => {
        const customer = finalCustomers?.find(c => 
          c.id == doc.clientId || 
          c.id === doc.clientId || 
          c.id === String(doc.clientId)
        );
        
        // Enhanced customer name resolution for documents
        let customerName = 'Contact inconnu';
        
        if (doc.contactName && doc.contactName.trim()) {
          customerName = doc.contactName.trim();
        } else if (customer) {
          if (customer.company && customer.company.trim()) {
            customerName = customer.company.trim();
          } else if (customer.name && customer.name.trim()) {
            customerName = customer.name.trim();
          } else if (customer.firstName || customer.lastName) {
            const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
            if (fullName) {
              customerName = fullName;
            }
          } else if (customer.email) {
            customerName = customer.email.split('@')[0];
          }
        }
        
      const statusMap = {
        'paid': 'Payée',
        'delivered': 'Livrée', 
        'confirmed': 'Confirmée',
        'pending': 'En attente',
        'draft': 'Brouillon',
        'cancelled': 'Annulée'
      };
      
      return {
        date: new Date(doc.date).toLocaleDateString('fr-FR'),
        reference: doc.number,
        client: customerName,
                  amount: `${(doc.total || 0).toLocaleString('fr-FR').replace(/\s/g, ',')} DA`,
          status: statusMap[doc.status] || 'En attente',
          source: 'documents'
      };
    });
      allSalesData.push(...documentsData);
    }

    console.log('📊 Combined sales data:', {
      totalCount: allSalesData.length,
      fromSales: allSalesData.filter(s => s.source === 'sales').length,
      fromDocuments: allSalesData.filter(s => s.source === 'documents').length
    });

    // Sort by date (most recent first) and return top 10
    return allSalesData
      .sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-'));
        const dateB = new Date(b.date.split('/').reverse().join('-'));
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [sales, finalDocuments, finalCustomers]); // Include sales in dependencies

  // IMPROVED: Generate all sales data using SHARED transformation for consistency
  const transformedSalesData = useMemo(() => {
    console.log('🔄 Generating sales data using SHARED utility for consistency with SalesPage');
    
    if (!sales || sales.length === 0) {
      console.log('⚠️ No sales data available');
      return [];
    }
    
    // Use SHARED transformation utility - ensures exact same logic as SalesPage
    return transformSalesData(sales, finalCustomers);
  }, [sales, finalCustomers]);

  // Update chart data when sales data changes
  useEffect(() => {
    console.log('📊 SalesReports useEffect triggered', {
      productsLength: finalProducts?.length || 0,
      salesLength: sales?.length || 0,
      documentsLength: finalDocuments?.length || 0,
      trigger: 'data change'
    });
    
    // Debug actual data structure
    if (sales && sales.length > 0) {
      console.log('🔍 DEBUG Sales Structure:', {
        totalSales: sales.length,
        firstSale: sales[0],
        salesWithItems: sales.filter(s => s.items && Array.isArray(s.items) && s.items.length > 0).length,
        sampleSaleItems: sales[0]?.items
      });
    }
    
    // Don't process if no data is available
    if ((finalProducts?.length || 0) === 0 && (sales?.length || 0) === 0 && (finalDocuments?.length || 0) === 0) {
      console.log('📊 Waiting for data to load...');
      return;
    }
    
    // Générer les données de ventes par produit - USING SHARED TRANSFORMED DATA
    const generateProductSalesData = () => {
      console.log('🔍 Generating product sales data using SHARED transformed data:', { 
        productsLength: finalProducts?.length || 0, 
        transformedSalesLength: transformedSalesData?.length || 0 
      });
      
      if ((finalProducts?.length || 0) === 0) {
        console.log('⚠️ No products available for product sales report');
        return [];
      }
      
      if ((transformedSalesData?.length || 0) === 0) {
        console.log('⚠️ No sales data available for product sales report');
        return [];
      }
      
      // Calculer les ventes par produit - using TRANSFORMED sales data
      const productSales = {};
      transformedSalesData.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            const productId = item.id || item.productId;
            if (!productId) {
              console.log('⚠️ Item without ID:', item);
              return;
            }
            
            if (!productSales[productId]) {
              const product = finalProducts.find(p => p.id === productId);
              console.log(`🔍 Looking for product ID: ${productId}`, {
                found: !!product,
                productName: product?.name || item.name,
                availableProductIds: finalProducts.map(p => p.id)
              });
              
              productSales[productId] = {
                product: product?.name || item.name || 'Produit inconnu',
                category: product?.category || 'Autre',
                quantitySold: 0,
                totalAmount: 0,
                profitMargin: product?.costPrice && product?.price ? 
                  Math.round(((product.price - product.costPrice) / product.price) * 100) + '%' : 'N/A'
              };
            }
            
            const quantity = parseInt(item.quantity) || 1;
            const total = parseInt(item.total) || (quantity * (parseInt(item.price) || 0));
            
            productSales[productId].quantitySold += quantity;
            productSales[productId].totalAmount += total;
          });
        }
      });
      
      // Convertir en tableau et trier par quantité vendue
      return Object.values(productSales)
        .map(p => ({
          ...p,
          totalAmount: `${p.totalAmount.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`
        }))
        .sort((a, b) => b.quantitySold - a.quantitySold)
        .slice(0, 10);
    };
    
    // Générer les données de ventes par client
    const generateClientSalesData = () => {
      console.log('🔍 Generating client sales data:', { 
        customersLength: finalCustomers?.length || 0, 
        salesLength: sales?.length || 0 
      });
      
      if ((finalCustomers?.length || 0) === 0) {
        console.log('⚠️ No customers available for client sales report');
        return [];
      }
      
      if ((sales?.length || 0) === 0) {
        console.log('⚠️ No sales data available for client sales report');
        return [];
      }
      
      // Calculer les ventes par client
      const clientSales = {};
      sales.forEach(sale => {
        const customerId = sale.customerId;
        if (!customerId) {
          console.log('⚠️ Sale without customer ID:', sale);
          return;
        }
        
        if (!clientSales[customerId]) {
          // Enhanced customer ID matching
          const customer = finalCustomers.find(c => 
            c.id == customerId || 
            c.id === customerId || 
            c.id === String(customerId)
          );
          
          console.log(`🔍 Looking for customer ID: ${customerId}`, {
            found: !!customer,
            customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'NOT FOUND',
            customerCompany: customer?.company,
            availableCustomerIds: finalCustomers.map(c => c.id)
          });
          
          // Enhanced client name resolution
          let clientName = 'Client Inconnu';
          
          if (sale.customerName && sale.customerName.trim()) {
            clientName = sale.customerName.trim();
          } else if (customer) {
            if (customer.company && customer.company.trim()) {
              clientName = customer.company.trim();
            } else if (customer.name && customer.name.trim()) {
              clientName = customer.name.trim();
            } else if (customer.firstName || customer.lastName) {
              const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
              if (fullName) {
                clientName = fullName;
              }
            } else if (customer.email) {
              clientName = customer.email.split('@')[0];
            }
          }
          
          clientSales[customerId] = {
            client: clientName,
            orderCount: 0,
            totalAmount: 0,
            lastOrder: '',
            orders: []
          };
        }
        
        const saleTotal = sale.items && Array.isArray(sale.items) ? 
          sale.items.reduce((sum, item) => sum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0) :
          parseFloat(sale.total) || 0;
        
        clientSales[customerId].orderCount += 1;
        clientSales[customerId].totalAmount += saleTotal;
        clientSales[customerId].orders.push({
          date: sale.date || sale.createdAt || new Date().toISOString(),
          total: saleTotal
        });
      });
      
      // Déterminer la date de dernière commande et le statut de fidélité
      Object.values(clientSales).forEach(client => {
        const sortedOrders = client.orders.sort((a, b) => new Date(b.date) - new Date(a.date));
        client.lastOrder = sortedOrders.length > 0 
          ? new Date(sortedOrders[0].date).toLocaleDateString('fr-FR')
          : 'N/A';
        
        // Déterminer le statut de fidélité
        client.loyalty = client.orderCount > 5 || client.totalAmount > 50000 ? 'Premium' : 'Standard';
        delete client.orders; // Nettoyer les données avant de retourner
      });
      
      // Convertir en tableau et trier par montant total
      const result = Object.values(clientSales)
        .map(c => ({
          ...c,
          totalAmount: `${c.totalAmount.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`
        }))
        .sort((a, b) => parseInt(b.totalAmount.replace(/[^\d]/g, '')) - parseInt(a.totalAmount.replace(/[^\d]/g, '')))
        .slice(0, 10);
      
      console.log('👥 Final client sales data:', result);
      return result;
    };
    
    // REMOVED: Seller performance data - not available in real sales data
    // Real sales from the sales form don't contain seller/salesperson information
    
    // Générer les données de tendances de ventes
    const generateSalesTrends = () => {
      console.log('📈 Generating sales trends with period filter:', periodFilter);
      
      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      
      // Adapter la période selon le filtre sélectionné
      if (periodFilter === 'week') {
        // Affichage par semaines pour les 12 dernières semaines
        const weeks = [];
        const currentDate = new Date();
        
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(currentDate);
          weekStart.setDate(currentDate.getDate() - (i * 7));
          const weekNumber = Math.ceil((weekStart.getDate()) / 7);
          
          // Combine sales from both sources for weekly data
          const weekSalesFromContext = sales
            ?.filter(sale => {
              const saleDate = new Date(sale.date || sale.createdAt || Date.now());
              const weekDiff = Math.floor((currentDate - saleDate) / (7 * 24 * 60 * 60 * 1000));
              return weekDiff === i;
            })
            .reduce((sum, sale) => {
              const amount = sale.items && Array.isArray(sale.items) 
                ? sale.items.reduce((itemSum, item) => itemSum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
                : parseFloat(sale.total || 0);
              return sum + amount;
            }, 0) || 0;

          const weekSalesFromDocuments = finalDocuments
            ?.filter(doc => {
              if (doc.type !== 'invoice' || doc.status === 'cancelled') return false;
              const docDate = new Date(doc.date);
              const weekDiff = Math.floor((currentDate - docDate) / (7 * 24 * 60 * 60 * 1000));
              return weekDiff === i;
            })
            .reduce((sum, doc) => sum + (doc.total || 0), 0) || 0;

          const weekSales = weekSalesFromContext + weekSalesFromDocuments;
          
          weeks.push({
            name: `S${weekNumber}`,
            [currentYear]: weekSales,
            [lastYear]: 0 // Les données de l'année précédente ne sont pas pertinentes pour les semaines
          });
        }
        return weeks;
      } else if (periodFilter === 'day') {
        // Affichage par jours pour les 30 derniers jours
        const days = [];
        const currentDate = new Date();
        
        for (let i = 29; i >= 0; i--) {
          const dayDate = new Date(currentDate);
          dayDate.setDate(currentDate.getDate() - i);
          
          // Combine sales from both sources for daily data
          const daySalesFromContext = sales
            ?.filter(sale => {
              const saleDate = new Date(sale.date || sale.createdAt || Date.now());
              return saleDate.toDateString() === dayDate.toDateString();
            })
            .reduce((sum, sale) => {
              const amount = sale.items && Array.isArray(sale.items) 
                ? sale.items.reduce((itemSum, item) => itemSum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
                : parseFloat(sale.total || 0);
              return sum + amount;
            }, 0) || 0;

          const daySalesFromDocuments = finalDocuments
            ?.filter(doc => {
              if (doc.type !== 'invoice' || doc.status === 'cancelled') return false;
              const docDate = new Date(doc.date);
              return docDate.toDateString() === dayDate.toDateString();
            })
            .reduce((sum, doc) => sum + (doc.total || 0), 0) || 0;

          const daySales = daySalesFromContext + daySalesFromDocuments;
          
          days.push({
            name: `${dayDate.getDate()}/${dayDate.getMonth() + 1}`,
            [currentYear]: daySales,
            [lastYear]: 0
          });
        }
        return days;
      } else {
        // Affichage par mois (par défaut)
        const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
        
      const result = months.map((month, idx) => {
        // Calculer les ventes réelles à partir des documents de vente
        let thisYearSales = 0;
        let lastYearSales = 0;
        
        // FIXED: Combine sales from both AppDataContext and DocumentsContext
        
        // Calculer les ventes depuis les sales (AppDataContext)
        const thisYearSalesFromContext = sales
          ?.filter(sale => {
            const saleDate = new Date(sale.date || sale.createdAt || Date.now());
            return saleDate.getFullYear() === currentYear && saleDate.getMonth() === idx;
          })
          .reduce((sum, sale) => {
            // Calculate from items if available, otherwise use total
            const amount = sale.items && Array.isArray(sale.items) 
              ? sale.items.reduce((itemSum, item) => itemSum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
              : parseFloat(sale.total || 0);
            return sum + amount;
          }, 0) || 0;

        // Calculer les ventes depuis les documents (factures)
        const thisYearInvoices = finalDocuments
          ?.filter(doc => {
            if (doc.type !== 'invoice' || doc.status === 'cancelled') return false;
            const docDate = new Date(doc.date);
            return docDate.getFullYear() === currentYear && docDate.getMonth() === idx;
          }) || [];
        
        const thisYearSalesFromDocuments = thisYearInvoices.reduce((sum, doc) => sum + (doc.total || 0), 0);
        
        // Combiner les deux sources
        thisYearSales = thisYearSalesFromContext + thisYearSalesFromDocuments;
        
        // Calculer les ventes de l'année précédente (similaire)
        const lastYearSalesFromContext = sales
          ?.filter(sale => {
            const saleDate = new Date(sale.date || sale.createdAt || Date.now());
            return saleDate.getFullYear() === lastYear && saleDate.getMonth() === idx;
          })
          .reduce((sum, sale) => {
            const amount = sale.items && Array.isArray(sale.items) 
              ? sale.items.reduce((itemSum, item) => itemSum + (parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0))), 0)
              : parseFloat(sale.total || 0);
            return sum + amount;
          }, 0) || 0;

        const lastYearInvoices = finalDocuments
          ?.filter(doc => {
            if (doc.type !== 'invoice' || doc.status === 'cancelled') return false;
            const docDate = new Date(doc.date);
            return docDate.getFullYear() === lastYear && docDate.getMonth() === idx;
          }) || [];
        
        const lastYearSalesFromDocuments = lastYearInvoices.reduce((sum, doc) => sum + (doc.total || 0), 0);
        
        lastYearSales = lastYearSalesFromContext + lastYearSalesFromDocuments;
        
        return {
          name: month,
          [currentYear]: thisYearSales,
          [lastYear]: lastYearSales,
          sales: thisYearSales,
          period: month,
          count: 1
        };
      });
      
      console.log('📈 Sales trends generated:', result.slice(-3)); // Log last 3 months
      return result;
      }
    };
    
    // Générer les données de ventes par catégorie
    const generateSalesByCategory = () => {
      console.log('🔍 Generating sales by category data', {
        productsCount: finalProducts?.length || 0,
        salesCount: sales?.length || 0,
        productsSample: finalProducts?.[0],
        salesSample: sales?.[0]
      });
      
      if ((finalProducts?.length || 0) === 0 || (sales?.length || 0) === 0) {
        console.log('⚠️ No data available for sales by category report');
        return [];
      }
      
      // Calculer les ventes par catégorie
      const categorySales = {};
      sales.forEach(sale => {
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            const productId = item.id || item.productId;
            const product = finalProducts.find(p => p.id === productId);
            
            console.log(`🏷️ Processing item ${item.name} (${productId}):`, {
              productFound: !!product,
              productCategory: product?.category,
              itemCategory: item.category
            });
            
            // Use product category if found, otherwise try item category, fallback to 'Autre'
            const category = product?.category || item.category || 'Autre';
            if (!categorySales[category]) {
              categorySales[category] = 0;
            }
            
            const total = parseInt(item.total) || (parseInt(item.quantity || 1) * parseInt(item.price || 0));
            categorySales[category] += total;
            
            console.log(`   → Added ${total} DA to category '${category}'`);
          });
        }
      });
      
      console.log('📊 Final category sales:', categorySales);
      
      // Convertir en tableau pour le graphique
      const result = Object.entries(categorySales)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
        
      console.log('📈 Category chart data:', result);
      return result;
    };
    
    // Générer les données des produits les plus vendus
    const generateTopProducts = () => {
      console.log('🔍 Generating top products data');
      console.log('🔍 Available data:', {
        salesCount: sales?.length || 0,
        documentsCount: finalDocuments?.length || 0,
        productsCount: finalProducts?.length || 0,
        salesSample: sales?.[0],
        documentsSample: finalDocuments?.[0]
      });
      
      // Calculer les ventes par produit depuis toutes les sources
      const productSales = {};
      
      // 1. Depuis les ventes (AppDataContext)
      if (sales && sales.length > 0) {
        console.log('📊 Processing sales data:', sales);
        sales.forEach((sale, saleIndex) => {
          console.log(`📊 Processing sale ${saleIndex}:`, sale);
          
        if (sale.items && Array.isArray(sale.items)) {
            console.log(`📊 Sale ${saleIndex} has ${sale.items.length} items:`, sale.items);
            sale.items.forEach((item, itemIndex) => {
              const productName = item.name || item.productName || item.product || `Produit ${item.id || itemIndex}`;
              const quantity = parseInt(item.quantity) || 1;
              const itemRevenue = parseFloat(item.total) || (quantity * parseFloat(item.price || 0));
              
              console.log(`📦 Item ${itemIndex}:`, {
                productName,
                quantity,
                itemRevenue,
                originalItem: item
              });
              
              if (!productSales[productName]) {
                productSales[productName] = {
                  quantity: 0,
                  revenue: 0
                };
              }
              
              productSales[productName].quantity += quantity;
              productSales[productName].revenue += itemRevenue;
            });
          } else {
            console.log(`⚠️ Sale ${saleIndex} has no items array or empty items:`, {
              hasItems: !!sale.items,
              itemsType: typeof sale.items,
              itemsLength: sale.items?.length,
              sale: sale
            });
            
            // If no items, try to use the sale total as a single product
            const fallbackProductName = sale.reference || sale.invoiceNumber || `Vente ${sale.id || saleIndex}`;
            const fallbackRevenue = parseFloat(sale.total) || 0;
            
            if (fallbackRevenue > 0) {
              if (!productSales[fallbackProductName]) {
                productSales[fallbackProductName] = {
                  quantity: 0,
                  revenue: 0
                };
              }
              productSales[fallbackProductName].quantity += 1;
              productSales[fallbackProductName].revenue += fallbackRevenue;
              
              console.log(`📦 Added fallback product:`, {
                productName: fallbackProductName,
                quantity: 1,
                revenue: fallbackRevenue
              });
            }
          }
        });
      } else {
        console.log('⚠️ No sales data available:', { sales, salesCount: sales?.length });
      }
      
      // 2. Depuis les documents (factures, devis)
      if (finalDocuments && finalDocuments.length > 0) {
        finalDocuments.forEach(doc => {
          if ((doc.type === 'invoice' || doc.type === 'quote') && doc.status !== 'cancelled') {
            if (doc.items && Array.isArray(doc.items)) {
              doc.items.forEach(item => {
                const productName = item.name || item.productName || `Produit ${item.id || 'Inconnu'}`;
                const quantity = parseInt(item.quantity) || 1;
                const itemRevenue = parseFloat(item.total) || (quantity * parseFloat(item.price || 0));
                
                if (!productSales[productName]) {
                  productSales[productName] = {
                    quantity: 0,
                    revenue: 0
                  };
                }
                
                productSales[productName].quantity += quantity;
                productSales[productName].revenue += itemRevenue;
              });
            }
          }
        });
      }
      
      // Convertir en tableau et retourner les top produits
      const result = Object.entries(productSales)
        .map(([name, data]) => ({
          name,
          quantity: data.quantity,
          revenue: data.revenue,
          value: data.quantity, // For compatibility with existing chart code
          percentage: 0 // Will be calculated after sorting
        }))
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10);
      
      // Calculate percentages based on top product
      const topQuantity = result[0]?.quantity || 1;
      const finalResult = result.map((product, index) => ({
        ...product,
        percentage: Math.round((product.quantity / topQuantity) * 100)
      }));
      
      console.log('🏆 Top products generated:', finalResult);
      return finalResult;
    };
    
    // Mettre à jour les états avec les données générées
    // Use TRANSFORMED sales data for consistency with SalesPage
    setSalesData(transformedSalesData.slice(0, 10)); // Limit to 10 for performance
    setProductSalesData(generateProductSalesData());
    setClientSalesData(generateClientSalesData());
    // REMOVED: No seller performance data since it's not available in real sales
    setSalesTrends(generateSalesTrends());
    setSalesByCategory(generateSalesByCategory());
    setTopProducts(generateTopProducts());
    
  }, [finalProducts, finalCustomers, finalDocuments, sales, purchases, periodFilter, transformedSalesData, dataRefreshKey]);
  
  const handleForceRefresh = () => {
    console.log('🔄 Force refreshing reports data...');
    
    // Debug current state
    console.log('🔍 CURRENT DATA STATE:', {
      sales: sales?.length || 0,
      finalProducts: finalProducts?.length || 0,
      finalDocuments: finalDocuments?.length || 0,
      finalCustomers: finalCustomers?.length || 0,
      topProducts: topProducts?.length || 0,
      salesTrends: salesTrends?.length || 0,
      salesData: salesData?.length || 0
    });
    
    if (sales && sales.length > 0) {
      console.log('🔍 SALES SAMPLE:', sales[0]);
    }
    
    setDataRefreshKey(prev => prev + 1);
    toast.success('Données actualisées! Vérifiez la console pour les détails.');
  };
  
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting PDF generation...');
      console.log('📊 Data check:', {
        salesData: salesData?.length || 0,
        rawSales: sales?.length || 0,
        topProducts: topProducts?.length || 0,
        clientSalesData: clientSalesData?.length || 0,
        salesTrends: salesTrends?.length || 0,
        salesByCategory: salesByCategory?.length || 0,
        salesSample: sales?.[0]
      });
      
      // Minimalist red theme colors
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            console.log('📄 Sales: Adding company logo to PDF...');
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title) => {
        if (yPosition > 250) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 25; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        yPosition += 5;
        
        // Simple clean line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPosition, 80, yPosition);
        
        yPosition += 10;
        
        // Reset to black for content
        pdf.setTextColor(0, 0, 0);
      };
      
      // Add header/footer to first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // 1. MINIMAL TITLE
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT DES VENTES', 20, yPosition);
      yPosition += 10;
      
      // Simple date info
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(`${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 15;
      
      // 2. EXECUTIVE SUMMARY
      await addSectionHeader('RÉSUMÉ EXÉCUTIF');
      
      const totalSales = sales?.length || 0;
      
      // Calculate total revenue from raw sales data for accuracy
      let totalRevenue = 0;
      if (sales && sales.length > 0) {
        try {
          totalRevenue = sales.reduce((sum, sale) => {
            let saleAmount = 0;
            if (sale.items && Array.isArray(sale.items)) {
              // Calculate from items for accuracy
              saleAmount = sale.items.reduce((itemSum, item) => {
                const itemTotal = parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0));
                return itemSum + itemTotal;
              }, 0);
            } else {
              // Fallback to sale total
              saleAmount = parseFloat(sale.total) || parseFloat(sale.amount) || 0;
            }
            return sum + saleAmount;
          }, 0);
          
          console.log('📊 PDF Revenue calculation:', {
            totalSales,
            totalRevenue,
            salesWithItems: sales.filter(s => s.items && s.items.length > 0).length
          });
        } catch (e) {
          console.log('Revenue calculation error:', e);
          totalRevenue = 0;
        }
      }
      
      const topProduct = topProducts[0];
      const topClient = clientSalesData[0];
      
      // Remove decorative boxes - keep it minimal
      
      const summaryData = [
        ['Nombre total de ventes', totalSales.toString()],
        ['Chiffre d\'affaires total', `${totalRevenue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`],
        ['Produit le plus vendu', topProduct ? `${topProduct.name || 'N/A'} (${topProduct.quantity || 0} unités)` : 'Aucune donnée'],
        ['Meilleur client', topClient ? `${topClient.client || 'N/A'} (${topClient.amount || '0 DA'})` : 'Aucune donnée'],
        ['Période d\'analyse', 'Toutes les données disponibles']
      ];
      
      console.log('📄 Sales: About to create summary table with data:', summaryData);
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Métrique', 'Valeur']],
          body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 9, 
          fontStyle: 'bold',
          halign: 'left'
        },
        styles: { 
          fontSize: 8, 
          cellPadding: 3,
          lineColor: [220, 220, 220],
          lineWidth: 0.1
        },
        margin: { left: 20, right: 20 }
      });
      console.log('✅ Sales: Summary table created successfully');
      } catch (tableError) {
        console.error('❌ Sales: Error creating summary table:', tableError);
        throw tableError;
      }
      
      yPosition = pdf.lastAutoTable.finalY + 20;
      
      // 3. RECENT SALES
      await addSectionHeader('VENTES RÉCENTES');
      
      if (memoizedSalesData.length > 0) {
        const recentSalesData = memoizedSalesData.slice(0, 10).map((sale, index) => [
          (index + 1).toString(),
          sale.date || 'N/A',
          sale.reference || 'N/A', 
          sale.client || 'Client inconnu',
          sale.amount || '0 DA',
          sale.status || 'N/A'
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Date', 'Référence', 'Client', 'Montant', 'Statut']],
          body: recentSalesData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            fontStyle: 'bold',
            halign: 'left'
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2,
            lineColor: [230, 230, 230],
            lineWidth: 0.1
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 20;
      } else {
        pdf.setFontSize(11);
        pdf.text('Aucune donnée de vente disponible', 20, yPosition);
        yPosition += 20;
      }
      
      // 4. TOP PRODUCTS (if available)
      if (sales && sales.length > 0) {
        await addSectionHeader('TOP PRODUITS');
        
        // Calculate product sales from raw sales data for accurate calculations
        const productSalesCalculation = {};
        
        sales.forEach(sale => {
          if (sale.items && Array.isArray(sale.items)) {
            sale.items.forEach(item => {
              const productName = item.name || item.productName || item.product || `Produit ${item.id || 'Inconnu'}`;
              const quantity = parseInt(item.quantity) || 1;
              const itemTotal = parseFloat(item.total) || (quantity * parseFloat(item.price || 0));
              
              if (!productSalesCalculation[productName]) {
                productSalesCalculation[productName] = {
                  quantity: 0,
                  revenue: 0
                };
              }
              
              productSalesCalculation[productName].quantity += quantity;
              productSalesCalculation[productName].revenue += itemTotal;
            });
          } else {
            // Fallback for sales without detailed items
            const productName = sale.reference || sale.invoiceNumber || `Vente ${sale.id || 'Inconnu'}`;
            const saleAmount = parseFloat(sale.total) || parseFloat(sale.amount) || 0;
            
            if (saleAmount > 0) {
              if (!productSalesCalculation[productName]) {
                productSalesCalculation[productName] = {
                  quantity: 0,
                  revenue: 0
                };
              }
              productSalesCalculation[productName].quantity += 1;
              productSalesCalculation[productName].revenue += saleAmount;
            }
          }
        });
        
        console.log('🏆 Product sales calculation (PDF):', productSalesCalculation);
        
        // Convert to array and get top products
        const topProductsFromSales = Object.entries(productSalesCalculation)
          .map(([name, data]) => ({
            name,
            quantity: data.quantity,
            revenue: data.revenue
          }))
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 8);
          
        console.log('🏆 Top products for PDF:', topProductsFromSales);
        
        const productsTableData = topProductsFromSales.map((product, index) => [
          (index + 1).toString(),
          product.name || 'N/A',
          product.quantity.toString(),
                      `${product.revenue.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Rang', 'Produit', 'Quantité', 'CA']],
          body: productsTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            fontStyle: 'bold',
            halign: 'left'
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2,
            lineColor: [230, 230, 230],
            lineWidth: 0.1
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 20;
      }
      
      // 5. SALES BY CLIENT (if available)
      if (sales && sales.length > 0) {
        await addSectionHeader('VENTES PAR CLIENT');
        
        // Calculate client sales from raw sales data for accurate amounts
        const clientSalesCalculation = {};
        
        sales.forEach((sale, index) => {
          console.log(`🔍 Processing sale ${index + 1}:`, {
            saleId: sale.id,
            saleTotal: sale.total,
            saleAmount: sale.amount,
            customerId: sale.customerId,
            customerName: sale.customerName,
            items: sale.items,
            itemsCount: sale.items?.length || 0,
            rawSale: sale
          });
          
          // Get client name with proper fallback logic
          const clientName = sale.customerName || sale.client || 
                           (finalCustomers?.find(c => c.id === sale.customerId)?.company) ||
                           (finalCustomers?.find(c => c.id === sale.customerId)?.name) ||
                           'Contact inconnu';
          
          if (!clientSalesCalculation[clientName]) {
            clientSalesCalculation[clientName] = {
              sales: 0,
              totalAmount: 0
            };
          }
          
          clientSalesCalculation[clientName].sales += 1;
          
          // Calculate total amount from items for accuracy
          let saleAmount = 0;
          if (sale.items && Array.isArray(sale.items)) {
            console.log(`📊 Calculating from items for ${clientName}:`, sale.items);
            saleAmount = sale.items.reduce((sum, item) => {
              const itemTotal = parseFloat(item.total) || (parseFloat(item.quantity || 1) * parseFloat(item.price || 0));
              console.log(`  - Item: ${item.name}, quantity: ${item.quantity}, price: ${item.price}, total: ${item.total}, calculated: ${itemTotal}`);
              return sum + itemTotal;
            }, 0);
          } else {
            // Fallback to sale total
            saleAmount = parseFloat(sale.total) || parseFloat(sale.amount) || 0;
            console.log(`📊 Using sale total for ${clientName}: ${saleAmount} (from sale.total: ${sale.total}, sale.amount: ${sale.amount})`);
          }
          
          console.log(`💰 Adding ${saleAmount} to ${clientName} (previous total: ${clientSalesCalculation[clientName].totalAmount})`);
          clientSalesCalculation[clientName].totalAmount += saleAmount;
        });
        
        console.log('👥 Client sales calculation (PDF):', clientSalesCalculation);
        
        // Convert to array and get top clients
        const topClientsFromSales = Object.entries(clientSalesCalculation)
          .map(([client, data]) => ({
            client,
            sales: data.sales,
            amount: `${data.totalAmount.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`
          }))
          .sort((a, b) => b.totalAmount - a.totalAmount) // Sort by amount, not sales count
          .slice(0, 8);
        
        const clientsTableData = topClientsFromSales.map((client, index) => [
          (index + 1).toString(),
          client.client,
          client.sales.toString(),
          client.amount
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Rang', 'Client', 'Nb. achats', 'Montant total']],
          body: clientsTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            fontStyle: 'bold',
            halign: 'left'
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2,
            lineColor: [230, 230, 230],
            lineWidth: 0.1
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 20;
      }
      
      // 6. FINAL PAGE SETUP
      // Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Rapport_Ventes_Complet_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      console.log('📄 Sales: About to save PDF with filename:', fileName);
      pdf.save(fileName);
      console.log('📄 Sales: PDF save command executed');
      
      setIsGenerating(false);
      toast.success('Rapport PDF complet généré avec succès!');
      console.log('✅ PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Rendu du contenu du rapport
  const renderReportContent = () => {
    switch (selectedReport) {
      case 'sales-by-period':
        return (
          <div className="space-y-6">
            {/* Graphique des ventes */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-white">Évolution des ventes</h4>
                <div className="flex items-center space-x-2">
                  <select 
                    value={periodFilter} 
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-sm text-white focus:outline-none focus:border-white/40"
                  >
                    <option value="month">Par mois</option>
                    <option value="week">Par semaine</option>
                    <option value="day">Par jour</option>
                  </select>
                  <button
                    onClick={handleForceRefresh}
                    className="p-1 text-white/60 hover:text-white transition-colors"
                    title="Actualiser les données"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="h-80">
                {salesTrends && salesTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={salesTrends}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                    <YAxis stroke="rgba(255,255,255,0.6)" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }}
                      itemStyle={{ color: 'white' }}
                      labelStyle={{ color: 'white' }}
                                                  formatter={(value) => [`${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`, '']}
                    />
                    <Legend wrapperStyle={{ color: 'white' }} />
                    <Bar dataKey={`${new Date().getFullYear()}`} name="Cette année" fill="#8884d8" />
                    <Bar dataKey={`${new Date().getFullYear() - 1}`} name="Année précédente" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-white/60">
                      <ChartBarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium mb-1">Aucune donnée de vente</p>
                      <p className="text-sm">Les données apparaîtront ici une fois que vous aurez créé des factures ou des ventes.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Tableau des ventes récentes */}
            <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
              <div className="p-4 border-b border-white/10">
                <h4 className="text-lg font-medium text-white">Ventes récentes</h4>
                <p className="text-sm text-white/60 mt-1">
                  {salesData.length > 0 
                    ? `${salesData.length} vente(s) trouvée(s)` 
                    : 'Aucune vente trouvée'
                  }
                </p>
              </div>
              {salesData.length > 0 ? (
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Référence</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Client</th>
                    <th className="px-4 py-3 text-right text-white font-medium">Montant</th>
                    <th className="px-4 py-3 text-center text-white font-medium">Statut</th>
                      <th className="px-4 py-3 text-center text-white font-medium">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {salesData.map((item, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-3 text-white">{item.date}</td>
                      <td className="px-4 py-3 text-white font-mono text-sm">{item.reference}</td>
                      <td className="px-4 py-3 text-white">{item.client}</td>
                      <td className="px-4 py-3 text-white text-right">{item.amount}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'Payée' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.source === 'sales' ? 'bg-purple-500/20 text-purple-300' :
                            'bg-blue-500/20 text-blue-300'
                          }`}>
                            {item.source === 'sales' ? 'Ventes' : 'Docs'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              ) : (
                <div className="p-8 text-center">
                  <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-white/30" />
                  <p className="text-white/60 text-lg font-medium mb-2">Aucune vente récente</p>
                  <p className="text-white/40 text-sm mb-4">
                    Les ventes créées dans la section "Ventes" apparaîtront ici automatiquement.
                  </p>
                  <Link 
                    to="/sales/new" 
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Créer une vente
                  </Link>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'sales-by-product':
        return (
          <div className="space-y-6">
            {/* Graphiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Graphique en camembert des ventes par catégorie */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-white">Ventes par catégorie</h4>
                  <button 
                    onClick={handleForceRefresh}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
                  >
                    🔄 Actualiser
                  </button>
                </div>
                <div className="h-80">
                  {salesByCategory.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={salesByCategory}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {salesByCategory.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                                                      formatter={(value) => `${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`}
                          contentStyle={{ 
                            backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                          }}
                        />
                        <Legend 
                          layout="vertical" 
                          verticalAlign="middle" 
                          align="right"
                          wrapperStyle={{ color: 'white' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white/60">
                      <ChartPieIcon className="h-16 w-16 mb-4 opacity-50" />
                      <p>Aucune donnée de catégorie disponible</p>
                      <p className="text-sm">Créez des ventes pour voir le graphique</p>
                      <button 
                        onClick={() => window.location.reload()} 
                        className="mt-2 px-3 py-1 bg-white/10 rounded text-sm hover:bg-white/20"
                      >
                        Actualiser
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Graphique en barres des produits les plus vendus */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h4 className="text-lg font-medium text-white mb-4">Top produits (quantité)</h4>
                <div className="h-80">
                  {topProducts.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={topProducts}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis type="number" stroke="rgba(255,255,255,0.6)" />
                        <YAxis dataKey="name" type="category" width={150} stroke="rgba(255,255,255,0.6)" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                          }}
                        />
                        <Bar dataKey="value" fill="#82ca9d" name="Quantité vendue" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white/60">
                      <ChartBarIcon className="h-16 w-16 mb-4 opacity-50" />
                      <p>Aucun produit vendu</p>
                      <p className="text-sm">Créez des ventes pour voir le top des produits</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tableau des ventes par produit */}
            <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
              <div className="p-4 border-b border-white/10">
                <h4 className="text-lg font-medium text-white">Détail des ventes par produit</h4>
              </div>
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Produit</th>
                    <th className="px-4 py-3 text-left text-white font-medium">Catégorie</th>
                    <th className="px-4 py-3 text-right text-white font-medium">Quantité vendue</th>
                    <th className="px-4 py-3 text-right text-white font-medium">Montant total</th>
                    <th className="px-4 py-3 text-right text-white font-medium">Marge</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {productSalesData.length > 0 ? (
                    productSalesData.map((item, index) => (
                      <tr key={index} className="hover:bg-white/5">
                        <td className="px-4 py-3 text-white">{item.product}</td>
                        <td className="px-4 py-3 text-white/80">{item.category}</td>
                        <td className="px-4 py-3 text-white text-right">{item.quantitySold}</td>
                        <td className="px-4 py-3 text-white text-right">{item.totalAmount}</td>
                        <td className="px-4 py-3 text-green-400 text-right">{item.profitMargin}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-white/60">
                        <div className="flex flex-col items-center">
                          <ChartBarIcon className="h-12 w-12 mb-2 opacity-50" />
                          <p>Aucune donnée de vente de produits disponible</p>
                          <p className="text-sm">Créez des ventes pour voir les rapports ici</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
        
      case 'sales-by-client':
        return (
          <div className="space-y-6">
            {/* Graphique des clients par montant total */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-white">Top clients par montant d'achat</h4>
                <button 
                  onClick={handleForceRefresh}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
                >
                  🔄 Actualiser
                </button>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={clientSalesData.slice(0, 5).map(client => ({
                      name: client.client,
                      value: parseInt(client.totalAmount.replace(/[^\d]/g, ''))
                    }))}
                    margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="name" 
                      stroke="rgba(255,255,255,0.6)"
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis stroke="rgba(255,255,255,0.6)" />
                    <Tooltip 
                                                  formatter={(value) => `${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`}
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }}
                    />
                    <Bar dataKey="value" fill="#8884d8" name="Montant total" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Tableau des ventes par client */}
            <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
              <div className="p-4 border-b border-white/10">
                <h4 className="text-lg font-medium text-white">Détail des ventes par client</h4>
              </div>
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-medium">Client</th>
                    <th className="px-4 py-3 text-center text-white font-medium">Nb commandes</th>
                    <th className="px-4 py-3 text-right text-white font-medium">Total achats</th>
                    <th className="px-4 py-3 text-center text-white font-medium">Dernier achat</th>
                    <th className="px-4 py-3 text-center text-white font-medium">Fidélité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {clientSalesData.map((item, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-3 text-white font-medium">{item.client}</td>
                      <td className="px-4 py-3 text-white text-center">{item.orderCount}</td>
                      <td className="px-4 py-3 text-white text-right">{item.totalAmount}</td>
                      <td className="px-4 py-3 text-white text-center">{item.lastOrder}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.loyalty === 'Premium' 
                            ? 'bg-purple-500/20 text-purple-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {item.loyalty}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
        
      case 'sales-comparison':
        return (
          <div className="space-y-6">
            {/* Graphique de comparaison des ventes */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="text-lg font-medium text-white mb-4">Comparaison des ventes annuelles</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={salesTrends}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                    <YAxis stroke="rgba(255,255,255,0.6)" />
                    <Tooltip 
                                                  formatter={(value) => `${value.toLocaleString('fr-FR').replace(/\s/g, ',')} DA`}
                      contentStyle={{ 
                        backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }}
                    />
                    <Legend wrapperStyle={{ color: 'white' }} />
                    <Line 
                      type="monotone" 
                      dataKey={`${new Date().getFullYear()}`} 
                      name="Cette année" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={`${new Date().getFullYear() - 1}`} 
                      name="Année précédente" 
                      stroke="#82ca9d" 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* REMOVED: Mock KPI cards - Not connected to real sales data */}
            {/* These were showing hardcoded values like +24.5%, 12,350 DA, 58.2% */}
            
            {/* REMOVED: Performance des vendeurs - Not available in real sales data */}
            {/* Real sales from the sales form don't contain seller/salesperson information */}
          </div>
        );
        
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Rapport non disponible</h3>
            <p className="text-white/60 max-w-md">
              Le rapport sélectionné n'est pas disponible actuellement.
            </p>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with back button and master export */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Rapports de ventes</h1>
          <p className="text-white/60 text-sm mt-1">
            Analysez vos performances de vente et vos tendances
          </p>
        </div>
        </div>
        
        {/* Master PDF Export Button for entire Ventes section */}
        <button
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50 shadow-lg"
        >
          <DocumentTextIcon className="h-5 w-5" />
          <span>{isGenerating ? 'Génération...' : 'Exporter PDF Complet'}</span>
        </button>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left sidebar with report types */}
        <div className="md:col-span-3">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">Types de rapports</h3>
            
            <div className="space-y-2">
              {reportTypes.map((report) => (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`cursor-pointer p-4 rounded-xl transition-colors ${
                    selectedReport === report.id
                      ? 'bg-gradient-to-r from-green-500/30 to-green-600/30 border border-green-500/50'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <h4 className="font-medium text-white">{report.name}</h4>
                  <p className="text-sm text-white/60 mt-1">{report.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right content area */}
        <div className="md:col-span-9">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">
                {reportTypes.find(r => r.id === selectedReport)?.name}
              </h3>
              

            </div>
            
            {/* Report content */}
            {renderReportContent()}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SalesReports; 